import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import type { PremiumPurchase } from '@root/entities/premium/model'

type PremiumPurchaseTableStatusCellProps = {
  purchase: PremiumPurchase.Purchase
}

type DisplayStatus = 'success' | 'processing' | 'failed'

export const PremiumPurchaseTableStatusCell: React.FC<
  PremiumPurchaseTableStatusCellProps
> = ({ purchase }) => {
  const { t } = useTranslation()

  const status = useMemo(() => {
    return match(purchase.status)
      .returnType<DisplayStatus>()
      .with('active', () => 'success')
      .with('processing', () => 'processing')
      .with('failed', () => 'failed')
      .with('expired', () => 'success')
      .with('archived', () => 'success')
      .with('inactive', () => 'success')
      .exhaustive()
  }, [purchase.status])

  const label = useMemo(() => {
    return match(status)
      .returnType<string>()
      .with('success', () => t('premium.purchase.status.success'))
      .with('processing', () => t('premium.purchase.status.processing'))
      .with('failed', () => t('premium.purchase.status.failed'))
      .exhaustive()
  }, [status, t])

  return <Root data-status={status}>{label}</Root>
}

const Root = styled.div`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding-inline: 8px;
  border-radius: 4px;

  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  text-transform: capitalize;

  &[data-status='success'] {
    background: var(--success-3);
    color: var(--success-11);
  }

  &[data-status='processing'] {
    background: var(--background-3);
    color: var(--text-11);
  }

  &[data-status='failed'] {
    background: var(--error-3);
    color: var(--error-11);
  }
`
